import React, { useEffect, useRef, useState } from "react";
import { api, apiErr } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Volume2, StopCircle, Loader2, Download } from "lucide-react";
import Markdown from "../../components/Markdown";
import { speak, stopSpeaking } from "../../lib/speech";
import { cacheGet, cacheSet } from "../../lib/cache";
import { toast } from "sonner";

export default function NotesTab({ cls, subject, chapterId }) {
  const cacheKey = `notes:${cls}:${subject}:${chapterId}`;
  const [notes, setNotes] = useState(() => cacheGet(cacheKey));
  const [loading, setLoading] = useState(!cacheGet(cacheKey));
  const [speaking, setSpeaking] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [err, setErr] = useState(null);
  const contentRef = useRef(null);

  useEffect(() => {
    stopSpeaking(); setSpeaking(false);
    const cached = cacheGet(cacheKey);
    setNotes(cached);
    setLoading(!cached);
    setErr(null);
    api.get(`/notes/${cls}/${subject}/${chapterId}`)
      .then((r) => { setNotes(r.data); cacheSet(cacheKey, r.data); })
      .catch((e) => { if (!cached) setErr(apiErr(e)); })
      .finally(() => setLoading(false));
    return () => { stopSpeaking(); };
  }, [cls, subject, chapterId, cacheKey]);

  const toggleSpeak = () => {
    if (speaking) { stopSpeaking(); setSpeaking(false); return; }
    if (!notes?.content) return;
    const ok = speak(notes.content, {
      onStart: () => setSpeaking(true),
      onEnd: () => setSpeaking(false),
      onError: (e) => {
        setSpeaking(false);
        toast.error(`Read Aloud failed: ${e || "voice unavailable"}`);
      },
    });
    if (!ok) toast.error("Text-to-speech isn't supported on this browser.");
  };

  const downloadPdf = async () => {
    if (!notes?.content || downloading) return;
    setDownloading(true);
    try {
      const html2pdf = (await import("html2pdf.js")).default;
      const filename = `${notes.title || "notes"}-Class${cls}-${subject}.pdf`.replace(/\s+/g, "_");

      // Build a self-contained print-friendly element
      const escapeHtml = (s) =>
        String(s ?? "")
          .replace(/&/g, "&amp;")
          .replace(/</g, "&lt;")
          .replace(/>/g, "&gt;")
          .replace(/"/g, "&quot;")
          .replace(/'/g, "&#39;");
      const safeTitle = escapeHtml(notes.title || "AI-Generated Notes");
      const safeCls = escapeHtml(cls);
      const safeSubjectLabel = subject === "physics" ? "Physics" : "Chemistry";
      const wrapper = document.createElement("div");
      wrapper.style.padding = "36px";
      wrapper.style.background = "#FFFFFF";
      wrapper.style.color = "#111";
      wrapper.style.fontFamily = "'Figtree', system-ui, sans-serif";
      wrapper.style.maxWidth = "780px";
      wrapper.innerHTML = `
        <div style="border-bottom:2px solid #1A1A1A;padding-bottom:14px;margin-bottom:22px;">
          <div style="font-size:11px;letter-spacing:.2em;text-transform:uppercase;font-weight:800;color:#555;">
            BrimiAI · ScienceLab &nbsp;·&nbsp; Class ${safeCls} ${safeSubjectLabel}
          </div>
          <div style="font-size:26px;font-weight:900;letter-spacing:-0.01em;margin-top:6px;">
            ${safeTitle}
          </div>
        </div>
        <div id="__pdf_body"></div>
        <div style="margin-top:36px;padding-top:12px;border-top:1px solid #ccc;font-size:10px;color:#777;">
          Generated by BrimiAI · ScienceLab — brimi.in
        </div>
      `;

      // Clone the currently rendered content (has KaTeX already parsed)
      const source = contentRef.current;
      if (source) {
        const clone = source.cloneNode(true);
        wrapper.querySelector("#__pdf_body").appendChild(clone);
      }

      document.body.appendChild(wrapper);
      await html2pdf()
        .set({
          margin: [10, 10, 10, 10],
          filename,
          image: { type: "jpeg", quality: 0.95 },
          html2canvas: { scale: 2, useCORS: true, backgroundColor: "#FFFFFF" },
          jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
          pagebreak: { mode: ["avoid-all", "css", "legacy"] },
        })
        .from(wrapper)
        .save();
      document.body.removeChild(wrapper);
      toast.success("PDF downloaded!");
    } catch (e) {
      console.error(e);
      toast.error("Could not generate PDF. Try again.");
    } finally { setDownloading(false); }
  };

  if (loading && !notes) return (
    <div className="brut-card p-8 mt-4 bg-white flex items-center gap-3">
      <Loader2 className="animate-spin" strokeWidth={2.5}/>
      <span className="font-bold">Generating detailed notes… (first time takes ~30s, then cached)</span>
    </div>
  );
  if (err && !notes) return (
    <div className="brut-card p-6 mt-4 bg-[#FFB3BA]" data-testid="notes-error">
      <div className="font-bold">Could not load notes</div>
      <div className="text-sm mt-1">{err}</div>
    </div>
  );

  return (
    <div className="brut-card p-6 sm:p-8 mt-4 bg-white" data-testid="notes-content">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <div className="text-xs font-bold tracking-[0.2em] uppercase">AI-Generated Notes</div>
          {loading && <Loader2 size={14} className="animate-spin" strokeWidth={2.5}/>}
        </div>
        <div className="flex gap-2">
          <Button
            onClick={downloadPdf}
            disabled={!notes?.content || downloading}
            data-testid="notes-download-pdf"
            className="brut-btn rounded-none bg-[#E9E6FA] text-black hover:bg-[#E9E6FA]">
            {downloading ? (
              <><Loader2 size={16} className="mr-1 animate-spin" strokeWidth={2.5}/>Generating…</>
            ) : (
              <><Download size={16} className="mr-1" strokeWidth={2.5}/>Download PDF</>
            )}
          </Button>
          <Button onClick={toggleSpeak} data-testid="notes-tts"
            className="brut-btn rounded-none bg-[#FFF5D1] text-black hover:bg-[#FFF5D1]">
            {speaking ? <><StopCircle size={16} className="mr-1" strokeWidth={2.5}/>Stop</>
                      : <><Volume2 size={16} className="mr-1" strokeWidth={2.5}/>Read Aloud</>}
          </Button>
        </div>
      </div>
      <div ref={contentRef}>
        <Markdown>{notes?.content}</Markdown>
      </div>
    </div>
  );
}
